/**
 * @fileoverview Molecule: the segmented control flipping between modes.
 * @module components/molecules/ModeSwitch
 */

import { MODE_LABEL, MODES } from "@/lib/domain"
import type { AppMode } from "@/types/types"

import "./ModeSwitch.css"

/** Props accepted by {@link ModeSwitch}. */
export interface ModeSwitchProps {
	/** The active mode. */
	readonly mode: AppMode
	/** Called when the inactive segment is picked. */
	readonly onSwitch: () => void
	/** Disables both segments, e.g. while the session is still loading. */
	readonly disabled?: boolean
}

/**
 * Mode picker.
 *
 * @description
 * Built from real buttons with `aria-pressed` rather than a checkbox: there
 * are two named states, and each deserves to be announced by its own name.
 * Picking the active segment is a no-op, so a double tap cannot flip twice.
 *
 * @param props - Active mode, switch callback and disabled flag.
 * @returns The rendered control.
 */
export function ModeSwitch({
	mode,
	onSwitch,
	disabled = false,
}: ModeSwitchProps) {
	return (
		<div className="mode-switch" role="group" aria-label="Mode">
			{MODES.map(candidate => {
				const isActive = candidate === mode

				return (
					<button
						key={candidate}
						type="button"
						className={`mode-switch__option mode-switch__option--${candidate}`}
						aria-pressed={isActive}
						disabled={disabled}
						onClick={() => {
							if (!isActive) onSwitch()
						}}
					>
						{MODE_LABEL[candidate]}
					</button>
				)
			})}
		</div>
	)
}
